import { Bot, Info } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCallback, useRef } from "react";

import { Badge } from "@/components/ui/badge";
import { InteractionSummary } from "@/lib/messaging";
import { MessageDisplay } from "@/components/pages/interactions/message-display";
import { MetricColorNumber } from "@/components/common/metrics-color-number";
import { MetricsList } from "@/lib/metrics";
import { PersonIcon } from "@radix-ui/react-icons";
import { TooltipWithIcon } from "@/components/tooltip-with-icon";
import { TopLevelModes } from "@/lib/modes";
import { useTranslation } from "react-i18next";

interface InteractionTableProps {
  data: InteractionSummary[];
  loading: boolean;
  hasMore: boolean;
  onIntersect: () => void;
  error?: Error | null;
}

export function InteractionsTable({
  data,
  loading,
  hasMore,
  error,
  onIntersect,
}: InteractionTableProps) {
  const { t } = useTranslation(["pages"]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        console.log("IntersectionObserver triggered");
        console.log(entries);
        if (entries[0].isIntersecting && hasMore) {
          console.log("Loading more data...");
          onIntersect();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, error],
  );

  // Formats the timestamp to the user's local date and time (YYYY-MM-DD and time on new line)
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const formattedDate = date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }); // YYYY-MM-DD
    const formattedTime = date.toLocaleTimeString();
    return (
      <>
        <div>{formattedDate}</div>
        <div>{formattedTime}</div>
      </>
    );
  };

  const formatMillisToMMSS = (millis: number) => {
    const totalSeconds = Math.floor(millis / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Determines the badge color based on the mode
  const getModeColor = (modeParam: string) => {
    switch (modeParam) {
      case TopLevelModes.Translate.valueOf():
        return "bg-translate-light text-translate-light dark:bg-translate-dark dark:text-translate-dark";
      case TopLevelModes.DailyLife.valueOf():
        return "bg-dailylife-light text-dailylife-light dark:bg-dailylife-dark dark:text-dailylife-dark";
      case TopLevelModes.ArgumentBattle.valueOf():
        return "bg-argumentbattle-light text-argumentbattle-light dark:bg-argumentbattle-dark dark:text-argumentbattle-dark";
      case TopLevelModes.ImproTest.valueOf():
        return "bg-improtest-light text-improtest-light dark:bg-improtest-dark dark:text-improtest-dark";
      case TopLevelModes.LangCoach.valueOf():
        return "bg-langcoach-light text-langcoach-light dark:bg-langcoach-dark dark:text-langcoach-dark";
      case TopLevelModes.Explainer.valueOf():
        return "bg-explainer-light text-explainer-light dark:bg-explainer-dark dark:text-explainer-dark";
      default:
    }
    return "bg-gray-200"; // Default color if mode not found
  };

  return (
    <div className="mt-10 w-full overflow-x-auto border rounded-lg shadow-sm">
      {/* Use custom tailwind classes so they are loaded by tailwind */}
      <div className="yellow-highlight" />
      <div className="green-highlight" />

      <Table className="min-w-[900px]">
        {" "}
        {/* Adjusted minimum width for horizontal scroll */}
        <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
          {" "}
          {/* Sticky header */}
          <TableRow>
            <TableHead className="text-center w-[150px]">
              {t("pages:interactions.table.date")}
            </TableHead>{" "}
            <TableHead className="text-center w-[150px]">
              {t("pages:interactions.table.mode")}
            </TableHead>{" "}
            <TableHead className="min-w-[300px]">
              {t("pages:interactions.table.message")}
            </TableHead>{" "}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((entry, index) => (
            <TableRow
              key={index}
              // ref={index === data.length - 1 ? lastElementRef : null}
            >
              <TableCell className="text-center whitespace-nowrap py-2">
                {formatTimestamp(entry.date)}
              </TableCell>{" "}
              {/* Date content moved back */}
              <TableCell>
                <div className="flex flex-col gap-2 items-center">
                  <div className="flex flex-row justify-between">
                    {entry.role === "assistant" ? (
                      <Bot className="h-4 w-4" />
                    ) : entry.role === "system" ? (
                      <Info className="h-4 w-4" />
                    ) : entry.role === "user" ? (
                      <PersonIcon className="h-4 w-4" />
                    ) : null}
                  </div>
                  <Badge className={getModeColor(entry.mode)}>
                    {entry.mode}
                  </Badge>
                  {entry.duration > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {formatMillisToMMSS(entry.duration)}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div className="flex flex-row items-center gap-8 pb-2">
                    {MetricsList.map((metric) =>
                      entry[metric.key as keyof InteractionSummary] !== -1 ? (
                        <div className="flex flex-row items-center gap-1">
                          <TooltipWithIcon
                            className="top-2 right-0"
                            content={t(metric.label)}
                            icon={metric.icon}
                            iconProps={{ size: 18 }}
                            iconClassName="stroke-muted-foreground"
                            side="top"
                            align="center"
                            tooltipClassName="p-2 bg-background border border-ternary"
                          />
                          <MetricColorNumber
                            value={
                              entry[
                                metric.key as keyof InteractionSummary
                              ] as number
                            }
                            size="xs"
                          />
                        </div>
                      ) : null,
                    )}
                  </div>
                  <MessageDisplay htmlContent={entry.message} />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {/* This row will be observed by the IntersectionObserver */}
          {hasMore && (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4">
                {" "}
                {/* colSpan adjusted back to 9 */}
                <div
                  ref={lastElementRef}
                  className="flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg
                      className="animate-spin h-5 w-5 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {loading
                    ? "Loading more data..."
                    : "Scroll down to load more"}
                </div>
              </TableCell>
            </TableRow>
          )}
          {!hasMore && data.length > 0 && (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4 text-gray-500">
                {" "}
                {/* colSpan adjusted back to 9 */}
                {t("pages:interactions.noMoreData")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
